import React, { useEffect, useState } from "react";
import ResponsiveModalAbsolute from "../../../components/ResponsiveModalAbsolute";
import Swal from "sweetalert2";
import { useAppSelector } from "../../../hooks/hooks";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import { plantillas } from "./ChatRoom";
import { motion } from "framer-motion";

interface Props {
    modalPlantilla: boolean;
    setModalPlantilla: React.Dispatch<React.SetStateAction<boolean>>;
    setPlantillas: React.Dispatch<React.SetStateAction<plantillas[]>>;
}

interface plantilla {
    mensaje: string;
    titulo: string;
    imagen: string;
    archivo: string;
    tipo: string;
}

interface imagenes {
    imagen: File;
    caption_imagen: string;
}

interface archivos {
    archivo: File;
    caption_archivo: string;
}

const ModalPlantilla = ({
    modalPlantilla,
    setModalPlantilla,
    setPlantillas,
}: Props) => {
    const casa = useAppSelector((state) => state.landing_house.house);
    const { id, tipo_usuario } = useAppSelector(
        (state) => state.usuario_logeado
    );

    const [tab, setTab] = useState<string>("mensaje");
    const [imagenes, setImagenes] = useState<imagenes[]>([]);
    const [archivos, setArchivos] = useState<archivos[]>([]);
    const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [errorImagen, setErrorImagen] = useState<string | null>(null);
    const [imagenSeleccionada, setImagenSeleccionada] = useState<number | null>(
        null
    );

    const [archivoSeleccionado, setArchivoSeleccionado] = useState<
        number | null
    >(null);

    const [plantilla, setPlantilla] = useState<plantilla>({
        mensaje: "",
        titulo: "",
        imagen: "",
        archivo: "",
        tipo: "",
    });

    useEffect(() => {
        setPlantilla({
            mensaje: "",
            titulo: "",
            imagen: "",
            archivo: "",
            tipo: "",
        });
        setPreviewImages([]);
        setImagenes([]);
        setArchivos([]);
        setTab("mensaje");
        setImagenSeleccionada(null);
        setArchivoSeleccionado(null);
    }, [modalPlantilla]);

    const guardarPlantilla = async () => {
        if (!plantilla.titulo.trim()) {
            Swal.fire({
                icon: "warning",
                title: "Campo requerido",
                text: "El título no puede estar vacío.",
            });
            return;
        }

        if (tab === "mensaje" && !plantilla.mensaje.trim()) {
            Swal.fire({
                icon: "warning",
                title: "Campo requerido",
                text: "El mensaje no puede estar vacío.",
            });
            return;
        }

        if (tab === "imagen") {
            // Validar si existe un error en la imagen
            if (errorImagen) {
                Swal.fire({
                    icon: "error",
                    title: "Imagen inválida",
                    text: errorImagen,
                });
                return;
            }

            // Validar si hay imagen cargada
            if (imagenes.length === 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Campo requerido",
                    text: "Debe seleccionar al menos una imagen antes de guardar.",
                });
                return;
            }
        }

        if (tab === "archivo") {
            if (errorArchivo) {
                Swal.fire({
                    icon: "error",
                    title: "Archivo inválido",
                    text: errorArchivo,
                });
                return;
            }

            if (archivos.length === 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Campo requerido",
                    text: "Debe seleccionar un archivo antes de guardar.",
                });
                return;
            }
        }

        let id_casa = 0;
        let id_corredor = 0;

        if (tipo_usuario === 6) {
            id_corredor = id;
        }

        if (tipo_usuario === 3 || tipo_usuario === 8) {
            id_casa = casa.id;
        }

        const formData = new FormData();
        formData.append("titulo", plantilla.titulo);
        formData.append("mensaje", plantilla.mensaje);
        if (tab === "imagen" && imagenes.length > 0) {
            imagenes.forEach((img, index) => {
                formData.append("imagen_whatsapp", img.imagen); // Importante: usa [] si el backend lo espera como array
                formData.append("caption_imagen", img.caption_imagen); // Importante: usa [] si el backend lo espera como array
            });
        }
        if (tab === "archivo" && archivos.length > 0) {
            archivos.forEach((file) => {
                formData.append("archivo_whatsapp", file.archivo); // Usa [] si el backend espera array
                formData.append("caption_archivo", file.caption_archivo); // Usa [] si el backend espera array
            });
        }
        formData.append("tipo", tab);
        formData.append("id_corredor", id_corredor.toString());
        formData.append("id_casa", id_casa.toString());

        try {
            // aquí tu llamada fetch/axios, por ejemplo:
            const response = await fetchAxiosToken({
                url: `/plantillas_whatsapp/post`,
                method: "post",
                body: formData,
            });

            Swal.fire({
                icon: "success",
                title: "Plantilla guardada",
                timer: 1500,
                showConfirmButton: false,
            });

            // Puedes cerrar el modal aquí si quieres:
            setModalPlantilla(false);
            getPlantillas();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "No se pudo guardar la plantilla.",
            });
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const validImages: imagenes[] = [];

        for (const file of files) {
            if (!file.type.startsWith("image/")) {
                setErrorImagen("Todos los archivos deben ser imágenes.");
                return;
            }

            validImages.push({
                imagen: file,
                caption_imagen: "", // caption vacío por defecto
            });
        }

        // Generar previsualizaciones
        validImages.forEach(({ imagen }) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImages((prev) => [...prev, reader.result as string]);
            };
            reader.readAsDataURL(imagen);
        });

        setErrorImagen(null);
        setImagenes((prev) => [...prev, ...validImages]);
    };

    const handleArchivoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const maxSize = 5 * 1024 * 1024; // 5MB
        const validFiles: archivos[] = [];

        for (const file of files) {
            if (file.size > maxSize) {
                setErrorArchivo(
                    `El archivo ${file.name} supera el límite de 5 MB.`
                );
                return;
            }
            validFiles.push({
                archivo: file,
                caption_archivo: "", // caption vacío por defecto
            });
        }

        setErrorArchivo(null);
        setArchivos((prev) => [...prev, ...validFiles]);
    };

    const getPlantillas = async () => {
        let id_casa = 0;
        let id_corredor = 0;

        if (tipo_usuario === 6) {
            id_corredor = id;
        }

        if (tipo_usuario === 3 || tipo_usuario === 8) {
            id_casa = casa.id;
        }

        const response = await fetchAxiosToken({
            url: `/plantillas_whatsapp/getAll/1/${id_casa}/${id_corredor}/?query=`,
        });

        setPlantillas(response.rows);
    };

    const handleRemoveImage = (index: number) => {
        setPreviewImages((prev) => prev.filter((_, i) => i !== index));
        setImagenes((prev) => prev.filter((_, i) => i !== index));
    };

    const handleRemoveArchivo = (index: number) => {
        setArchivos((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div>
            <ResponsiveModalAbsolute
                modalVisible={modalPlantilla}
                setModalVisible={setModalPlantilla}
            >
                <div className="flex flex-col justify-center items-center">
                    <div className="text-2xl text-center font-bold">
                        Mensaje de plantilla
                    </div>
                    <hr className="border  my-2 w-5/6 mx-auto" />
                    {imagenSeleccionada === null &&
                        archivoSeleccionado === null && (
                            <div className="w-5/6">
                                <div className="flex justify-center items-center gap-3 mt-5">
                                    <div
                                        onClick={() => setTab("mensaje")}
                                        className={` ${
                                            tab === "mensaje"
                                                ? " bg-[#029BFC] text-white "
                                                : "bg-white text-[#029BFC] border border-[#029BFC] "
                                        } p-2 cursor-pointer rounded-xl`}
                                    >
                                        Mensaje
                                    </div>
                                    <div
                                        onClick={() => setTab("imagen")}
                                        className={` ${
                                            tab === "imagen"
                                                ? " bg-[#029BFC] text-white "
                                                : "text-[#029BFC] border border-[#029BFC] "
                                        } p-2 cursor-pointer rounded-xl`}
                                    >
                                        Imagen
                                    </div>

                                    <div
                                        onClick={() => setTab("archivo")}
                                        className={` ${
                                            tab === "archivo"
                                                ? " bg-[#029BFC] text-white "
                                                : "text-[#029BFC] border border-[#029BFC] "
                                        } p-2 cursor-pointer rounded-xl`}
                                    >
                                        Archivo
                                    </div>
                                </div>

                                <div className="flex flex-col justify-start items-start w-full  mt-10 gap-3">
                                    <div className="font-semibold text-xl w-5/6 mx-auto">
                                        Titulo:
                                    </div>
                                    <div className=" text-base w-5/6 mx-auto">
                                        <input
                                            type="text"
                                            value={plantilla.titulo}
                                            className="p-2 rounded-lg border  w-full"
                                            onChange={(e) =>
                                                setPlantilla({
                                                    ...plantilla,
                                                    titulo: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                </div>

                                {tab === "mensaje" && (
                                    <>
                                        <div className="flex flex-col justify-start items-start w-full mt-10 gap-3">
                                            <div className="font-semibold text-xl w-5/6 mx-auto">
                                                Mensaje:
                                            </div>

                                            <div className="text-base w-5/6 mx-auto ">
                                                <textarea
                                                    value={plantilla.mensaje}
                                                    onChange={(e) => {
                                                        if (
                                                            e.target.value
                                                                .length <= 500
                                                        ) {
                                                            setPlantilla({
                                                                ...plantilla,
                                                                mensaje:
                                                                    e.target
                                                                        .value,
                                                            });
                                                        }
                                                    }}
                                                    className="p-2 rounded-lg border  w-full resize-none"
                                                    rows={5}
                                                    placeholder="Escribe tu mensaje..."
                                                />
                                                <div className="text-sm text-right text-gray-600 mt-1">
                                                    {plantilla.mensaje.length}
                                                    /500
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500 mt-2 w-5/6 mx-auto">
                                            <p className="font-semibold">
                                                Instrucciones para dar formato
                                                en WhatsApp:
                                            </p>
                                            <ul className="list-disc ml-4 mt-1">
                                                <li>
                                                    Para texto en{" "}
                                                    <strong>negrita</strong>,
                                                    use asteriscos:{" "}
                                                    <code>*Hola*</code>
                                                </li>
                                                <li>
                                                    Para texto en{" "}
                                                    <em>cursiva</em>, use
                                                    guiones bajos:{" "}
                                                    <code>_Hola_</code>
                                                </li>
                                                <li>
                                                    Para texto <s>tachado</s>,
                                                    use virgulillas:{" "}
                                                    <code>~Hola~</code>
                                                </li>
                                                <li>
                                                    Para{" "}
                                                    <code>monoespaciado</code>,
                                                    use tres acentos invertidos:{" "}
                                                    <code>```Hola```</code>
                                                </li>
                                            </ul>
                                        </div>
                                    </>
                                )}

                                {tab === "imagen" && (
                                    <div className="flex flex-col justify-start items-start w-full mt-10 gap-3">
                                        <div className="font-semibold text-xl w-5/6 mx-auto">
                                            Cargar imagen:
                                        </div>

                                        <div className="text-base w-5/6 mx-auto ">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={handleImageChange}
                                                className="p-2 rounded-lg border w-full bg-white cursor-pointer"
                                            />

                                            {errorImagen && (
                                                <div className="text-red-600 text-sm mt-2">
                                                    {errorImagen}
                                                </div>
                                            )}

                                            {previewImages.length > 0 &&
                                                !errorImagen && (
                                                    <div className="mt-4">
                                                        <p className="text-sm text-gray-700 mb-1">
                                                            Previsualizaciones:
                                                        </p>
                                                        <div className="flex flex-col justify-center items-center gap-4">
                                                            {previewImages.map(
                                                                (src, idx) => (
                                                                    <div
                                                                        onClick={() =>
                                                                            setImagenSeleccionada(
                                                                                idx
                                                                            )
                                                                        }
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="relative group hover:scale-105 transition-transform shadow-lg border w-[300px] py-5 duration-200  rounded-lg cursor-pointer flex flex-col justify-center items-center "
                                                                    >
                                                                        <img
                                                                            src={
                                                                                src
                                                                            }
                                                                            alt={`Preview ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            className="max-w-[150px] max-h-[150px] rounded-lg mx-auto"
                                                                        />
                                                                        <div className="mt-2 text-sm text-left whitespace-pre-wrap w-5/6  mx-auto">
                                                                            {
                                                                                imagenes[
                                                                                    idx
                                                                                ]
                                                                                    ?.caption_imagen
                                                                            }
                                                                        </div>
                                                                        <button
                                                                            onClick={(
                                                                                e
                                                                            ) => {
                                                                                e.stopPropagation(); // 🚫 evita que el click se propague al div padre
                                                                                handleRemoveImage(
                                                                                    idx
                                                                                );
                                                                            }}
                                                                            className="absolute top-1 right-1 p-1 bg-white border border-red-500 rounded-full shadow-md text-red-600 "
                                                                            title="Eliminar imagen"
                                                                        >
                                                                            <svg
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                strokeWidth="1.5"
                                                                                stroke="currentColor"
                                                                                className="w-6 h-6 text-red-500"
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                                                                />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                )}

                                {tab === "archivo" && (
                                    <div className="flex flex-col justify-start items-start w-full mt-10 gap-3">
                                        <div className="font-semibold text-xl w-5/6 mx-auto">
                                            Cargar archivo:
                                        </div>

                                        <div className="text-base w-5/6 mx-auto">
                                            <input
                                                type="file"
                                                multiple
                                                onChange={handleArchivoChange}
                                                className="p-2 rounded-lg border-2 w-full bg-white cursor-pointer"
                                            />

                                            {errorArchivo && (
                                                <div className="text-red-600 text-sm mt-2">
                                                    {errorArchivo}
                                                </div>
                                            )}

                                            {archivos.length > 0 &&
                                                !errorArchivo && (
                                                    <div className="mt-3 text-sm text-gray-700 w-full mx-auto">
                                                        <p className="mb-1">
                                                            Archivos
                                                            seleccionados:
                                                        </p>
                                                        <ul className="list-disc space-y-1">
                                                            {archivos.map(
                                                                (file, idx) => (
                                                                    <li
                                                                        onClick={() =>
                                                                            setArchivoSeleccionado(
                                                                                idx
                                                                            )
                                                                        }
                                                                        className="mt-2 cursor-pointer flex flex-col justify-center items-center px-2 py-3 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                                                        key={
                                                                            idx
                                                                        }
                                                                    >
                                                                        <div className="flex justify-between items-center w-full">
                                                                            <div>
                                                                                <strong>
                                                                                    Nombre:
                                                                                </strong>{" "}
                                                                                {
                                                                                    file
                                                                                        .archivo
                                                                                        .name
                                                                                }{" "}
                                                                                —{" "}
                                                                                {(
                                                                                    file
                                                                                        .archivo
                                                                                        .size /
                                                                                    1024
                                                                                ).toFixed(
                                                                                    2
                                                                                )}{" "}
                                                                                KB
                                                                            </div>

                                                                            <div>
                                                                                <button
                                                                                    onClick={(
                                                                                        e
                                                                                    ) => {
                                                                                        e.stopPropagation(); // 🚫 evita que el click se propague al li padre
                                                                                        handleRemoveArchivo(
                                                                                            idx
                                                                                        );
                                                                                    }}
                                                                                    className=" p-1 bg-white border border-red-500 rounded-full shadow-md text-red-600 mx-3 "
                                                                                    title="Eliminar archivo"
                                                                                >
                                                                                    <svg
                                                                                        fill="none"
                                                                                        viewBox="0 0 24 24"
                                                                                        strokeWidth="1.5"
                                                                                        stroke="currentColor"
                                                                                        className="w-5 h-5 text-red-500"
                                                                                    >
                                                                                        <path
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                                                                        />
                                                                                    </svg>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mt-2 border-t-2 py-3  text-sm text-left whitespace-pre-wrap w-5/6  mx-auto">
                                                                            {
                                                                                archivos[
                                                                                    idx
                                                                                ]
                                                                                    ?.caption_archivo
                                                                            }
                                                                        </div>
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                )}

                                <div
                                    onClick={() => guardarPlantilla()}
                                    className="w-5/6 mx-auto bg-[#029BFC] text-white rounded-lg p-2 text-lg mt-5 text-center cursor-pointer hover:scale-105 "
                                >
                                    Guardar
                                </div>
                            </div>
                        )}

                    {imagenSeleccionada !== null && (
                        // Vista de imagen seleccionada con textarea
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            className="w-full flex flex-col items-center gap-4"
                        >
                            <img
                                src={previewImages[imagenSeleccionada]}
                                alt="Imagen seleccionada"
                                className="w-auto h-[300px] rounded-xl border"
                            />
                            <textarea
                                className="w-5/6 max-w-xl p-2 border rounded-md mt-3"
                                placeholder="Escribe una descripción..."
                                value={
                                    imagenes[imagenSeleccionada]?.caption_imagen
                                }
                                rows={5}
                                onChange={(e) => {
                                    const nuevas = [...imagenes];
                                    nuevas[imagenSeleccionada].caption_imagen =
                                        e.target.value;
                                    setImagenes(nuevas);
                                }}
                            />

                            <div className="text-sm text-gray-500 mt-2">
                                <p className="font-semibold">
                                    Instrucciones para dar formato en WhatsApp:
                                </p>
                                <ul className="list-disc ml-4 mt-1">
                                    <li>
                                        Para texto en <strong>negrita</strong>,
                                        use asteriscos: <code>*Hola*</code>
                                    </li>
                                    <li>
                                        Para texto en <em>cursiva</em>, use
                                        guiones bajos: <code>_Hola_</code>
                                    </li>
                                    <li>
                                        Para texto <s>tachado</s>, use
                                        virgulillas: <code>~Hola~</code>
                                    </li>
                                    <li>
                                        Para <code>monoespaciado</code>, use
                                        tres acentos invertidos:{" "}
                                        <code>```Hola```</code>
                                    </li>
                                </ul>
                            </div>
                            <button
                                onClick={() => setImagenSeleccionada(null)}
                                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:scale-105"
                            >
                                Guardar
                            </button>
                        </motion.div>
                    )}

                    {archivoSeleccionado !== null && (
                        // Vista de imagen seleccionada con textarea
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            className="w-full flex flex-col items-center gap-4 mt-5"
                        >
                            <div className="text-lg px-2 py-1 bg-gray-200 rounded-md">
                                <strong>Nombre:</strong>{" "}
                                {archivos[archivoSeleccionado]?.archivo.name} —{" "}
                                {(
                                    archivos[archivoSeleccionado]?.archivo
                                        .size / 1024
                                ).toFixed(2)}{" "}
                                KB
                            </div>
                            <textarea
                                className="w-5/6 max-w-xl p-2 border rounded-md mt-3"
                                placeholder="Escribe una descripción..."
                                value={
                                    archivos[archivoSeleccionado]
                                        ?.caption_archivo
                                }
                                rows={5}
                                onChange={(e) => {
                                    const nuevas = [...archivos];
                                    nuevas[
                                        archivoSeleccionado
                                    ].caption_archivo = e.target.value;
                                    setArchivos(nuevas);
                                }}
                            />

                            <div className="text-sm text-gray-500 mt-2">
                                <p className="font-semibold">
                                    Instrucciones para dar formato en WhatsApp:
                                </p>
                                <ul className="list-disc ml-4 mt-1">
                                    <li>
                                        Para texto en <strong>negrita</strong>,
                                        use asteriscos: <code>*Hola*</code>
                                    </li>
                                    <li>
                                        Para texto en <em>cursiva</em>, use
                                        guiones bajos: <code>_Hola_</code>
                                    </li>
                                    <li>
                                        Para texto <s>tachado</s>, use
                                        virgulillas: <code>~Hola~</code>
                                    </li>
                                    <li>
                                        Para <code>monoespaciado</code>, use
                                        tres acentos invertidos:{" "}
                                        <code>```Hola```</code>
                                    </li>
                                </ul>
                            </div>
                            <button
                                onClick={() => setArchivoSeleccionado(null)}
                                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:scale-105"
                            >
                                Guardar
                            </button>
                        </motion.div>
                    )}
                </div>
            </ResponsiveModalAbsolute>
        </div>
    );
};

export default ModalPlantilla;
